import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
    },
  },
};

// 동이 문자로 고정되는 단지 (표시 시 「동」 접미사 추가)
const FIXED_BUILDING_BY_APARTMENT = {
  ForeonStation5: "스테이션5",
};

const SubmissionCheckPage = () => {
  const navigate = useNavigate();
  const apartmentCode = Cookies.get("apartmentCode") || "";
  const fixedBuilding = FIXED_BUILDING_BY_APARTMENT[apartmentCode] || "";
  const hasFixedBuilding = Boolean(fixedBuilding);

  const [formData, setFormData] = useState({
    building: fixedBuilding || "",
    unit: "",
    applicantName: "",
  });
  const [errors, setErrors] = useState({
    applicantName: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (fixedBuilding) {
      setFormData((prev) => ({
        ...prev,
        building: fixedBuilding,
      }));
    }
  }, [fixedBuilding]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "building" && hasFixedBuilding) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmissionCheck = async () => {
    // 에러 메시지 초기화
    setErrors({
      applicantName: "",
    });

    // 필수값 검증
    if (
      !formData.building.trim() ||
      !formData.unit.trim() ||
      !formData.applicantName.trim()
    ) {
      alert("모든 필수 항목을 입력해주세요.");
      return;
    }

    // 신청인명 유효성 검증 (자음/모음만 있는 경우 체크)
    const name = formData.applicantName.trim();

    // 완성된 한글이 아닌 자음/모음이 포함되어 있는지 체크
    // 완성된 한글(가-힣)이 아닌 자음(ㄱ-ㅎ)이나 모음(ㅏ-ㅣ)이 있으면 에러
    const hasIncompleteHangul = /[ㄱ-ㅎㅏ-ㅣ]/.test(name);

    if (hasIncompleteHangul) {
      setErrors((prev) => ({
        ...prev,
        applicantName: "이름을 정확히 입력해주세요.",
      }));
      return;
    }

    setIsLoading(true);

    try {
      // 카카오 소셜 로그인 URL 생성
      const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=773567d2c88e5caba57b5acb024de423&redirect_uri=${encodeURIComponent(
        "http://localhost:3000/submission-result"
      )}&response_type=code&state=submission_check`;

      // 카카오 로그인 페이지로 이동
      window.location.href = kakaoAuthUrl;
    } catch (error) {
      console.error("카카오 로그인 오류:", error);
      alert("로그인에 실패했습니다. 다시 시도해주세요.");
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      className="submission-check-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        height: "100vh",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        padding: "40px 24px 24px 24px",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      {/* 메인 콘텐츠 영역 */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          textAlign: "center",
          paddingTop: "0px",
          paddingBottom: "200px",
        }}
      >
        {/* 제목 */}
        <motion.div
          style={{
            marginTop: "40px",
          }}
          className="page-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              delay: 0.1,
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1],
            },
          }}
        >
          <h1>접수내역 확인</h1>
        </motion.div>

        {/* 폼 영역 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {/* 동 입력 필드 */}
          <div style={{ width: "100%" }}>
            <label
              style={{
                display: "block",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000000",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              동<span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              type="text"
              inputMode={hasFixedBuilding ? "text" : "numeric"}
              name="building"
              value={
                hasFixedBuilding
                  ? formatBuildingLabel(fixedBuilding, apartmentCode)
                  : formData.building
              }
              onChange={(e) => {
                if (hasFixedBuilding) return;
                // 숫자만 입력 허용
                const value = e.target.value.replace(/[^0-9]/g, "");
                setFormData((prev) => ({
                  ...prev,
                  building: value,
                }));
              }}
              placeholder="예: 101"
              readOnly={hasFixedBuilding}
              disabled={hasFixedBuilding}
              style={{
                width: "100%",
                height: "48px",
                padding: "0 16px",
                border: "2px solid #F3F4F6",
                borderRadius: "8px",
                fontSize: "16px",
                backgroundColor: hasFixedBuilding ? "#F9FAFB" : "#FFFFFF",
                color: hasFixedBuilding ? "#6B7280" : "#111827",
                boxSizing: "border-box",
                outline: "none",
                cursor: hasFixedBuilding ? "not-allowed" : "text",
              }}
              onFocus={(e) => {
                if (hasFixedBuilding) return;
                e.target.style.borderColor = "#7A4FFF";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#F3F4F6";
                e.target.style.outline = "none";
              }}
            />
            {hasFixedBuilding && (
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "13px",
                  color: "#6B7280",
                  textAlign: "left",
                }}
              >
                이 단지는 동이{" "}
                {formatBuildingLabel(fixedBuilding, apartmentCode)}로
                고정됩니다.
              </p>
            )}
          </div>

          {/* 호수 입력 필드 */}
          <div style={{ width: "100%" }}>
            <label
              style={{
                display: "block",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000000",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              호수<span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="unit"
              value={formData.unit}
              onChange={(e) => {
                // 숫자만 입력 허용
                const value = e.target.value.replace(/[^0-9]/g, "");
                setFormData((prev) => ({
                  ...prev,
                  unit: value,
                }));
              }}
              placeholder="예: 101"
              style={{
                width: "100%",
                height: "48px",
                padding: "0 16px",
                border: "2px solid #F3F4F6",
                borderRadius: "8px",
                fontSize: "16px",
                backgroundColor: "#FFFFFF",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#7A4FFF";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#F3F4F6";
                e.target.style.outline = "none";
              }}
            />
          </div>

          {/* 신청인 명 입력 필드 */}
          <div style={{ width: "100%" }}>
            <label
              style={{
                display: "block",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000000",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              신청인 명<span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              type="text"
              name="applicantName"
              value={formData.applicantName}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  applicantName: value,
                }));
                // 에러 메시지 초기화
                if (errors.applicantName) {
                  setErrors((prev) => ({
                    ...prev,
                    applicantName: "",
                  }));
                }
              }}
              placeholder="예: 홍길동"
              style={{
                width: "100%",
                height: "48px",
                padding: "0 16px",
                border: "2px solid #F3F4F6",
                borderRadius: "8px",
                fontSize: "16px",
                backgroundColor: "#FFFFFF",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#7A4FFF";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#F3F4F6";
                e.target.style.outline = "none";
              }}
            />
            {errors.applicantName && (
              <div
                style={{
                  color: "#FF0000",
                  fontSize: "14px",
                  marginTop: "4px",
                  textAlign: "left",
                }}
              >
                {errors.applicantName}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* 하단 카카오 로그인 버튼 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        style={{
          position: "fixed",
          bottom: "40px",
          left: "0",
          right: "0",
          padding: "20px 24px 40px",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingBottom: "env(safe-area-inset-bottom, 40px)",
        }}
      >
        <button
          onClick={handleSubmissionCheck}
          disabled={
            isLoading ||
            !formData.building.trim() ||
            !formData.unit.trim() ||
            !formData.applicantName.trim()
          }
          style={{
            width: "100%",
            height: "56px",
            backgroundColor:
              isLoading ||
              !formData.building.trim() ||
              !formData.unit.trim() ||
              !formData.applicantName.trim()
                ? "#F3F4F6"
                : "#FEE500",
            color: "#000000",
            border: "none",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            cursor:
              isLoading ||
              !formData.building.trim() ||
              !formData.unit.trim() ||
              !formData.applicantName.trim()
                ? "not-allowed"
                : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "background-color 0.2s ease",
            opacity: isLoading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (
              !isLoading &&
              formData.building.trim() &&
              formData.unit.trim() &&
              formData.applicantName.trim()
            ) {
              e.target.style.backgroundColor = "#FDD835";
            }
          }}
          onMouseLeave={(e) => {
            if (
              !isLoading &&
              formData.building.trim() &&
              formData.unit.trim() &&
              formData.applicantName.trim()
            ) {
              e.target.style.backgroundColor = "#FEE500";
            }
          }}
        >
          {/* 카카오 로고 이미지 */}
          <img
            src="/kakaoLogo.png"
            alt="카카오 로고"
            style={{
              width: "24px",
              height: "24px",
              objectFit: "contain",
            }}
          />
          {isLoading ? "처리 중..." : "접수내역 확인"}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default SubmissionCheckPage;
